import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'
import PageHeader from './PageHeader'
import Footer from './Footer'

const AdminHelpConversations = ({ onBack, onLogout, embedded = false }) => {
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [updating, setUpdating] = useState(null)

  useEffect(() => {
    loadConversations()
  }, [statusFilter])

  const loadConversations = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = statusFilter === 'all' ? {} : { status: statusFilter }
      const data = await apiService.listHelpConversations(params)
      setConversations(data.conversations || [])
    } catch (err) {
      console.error('Error loading help conversations:', err)
      setError(err.message || 'Failed to load help conversations')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (conversationId, newStatus) => {
    try {
      setUpdating(conversationId)
      await apiService.updateHelpConversationStatus(conversationId, newStatus)
      // Update local state
      setConversations(prev => prev.map(c =>
        c.conversation_id === conversationId ? { ...c, status: newStatus } : c
      ))
      if (selectedConversation?.conversation_id === conversationId) {
        setSelectedConversation(prev => ({ ...prev, status: newStatus }))
      }
    } catch (err) {
      console.error('Error updating help conversation status:', err)
      setError(err.message || 'Failed to update status')
    } finally {
      setUpdating(null)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-300 border-blue-500/30'
      case 'reviewed': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
      case 'resolved': return 'bg-green-500/20 text-green-300 border-green-500/30'
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30'
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // When embedded, render only the content without page wrapper
  const content = (
    <>
      {/* Filter Bar */}
        <div className="mb-6 flex items-center gap-4">
          <span className="text-sm text-gray-400">Filter by status:</span>
          <div className="flex gap-2">
            {['all', 'new', 'reviewed', 'resolved'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          <button
            onClick={loadConversations}
            className="ml-auto px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-300 mb-2">No help conversations yet</h3>
            <p className="text-gray-500">Help conversations from users will appear here</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversations List */}
            <div className="lg:col-span-1 space-y-3">
              {conversations.map(item => (
                <div
                  key={item.conversation_id}
                  onClick={() => setSelectedConversation(item)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedConversation?.conversation_id === item.conversation_id
                      ? 'bg-blue-900/30 border-blue-500/50'
                      : 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-sm text-white line-clamp-2 mb-1">
                        {item.summary || 'No summary available'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{item.user_email}</span>
                        <span>•</span>
                        <span>{formatDate(item.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Conversation Detail */}
            <div className="lg:col-span-2">
              {selectedConversation ? (
                <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden">
                  {/* Header */}
                  <div className="p-6 border-b border-gray-700/50">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium text-gray-300">
                            Help Conversation
                          </span>
                        </div>
                        <p className="text-white text-lg font-medium">
                          {selectedConversation.summary || 'Conversation details'}
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          From {selectedConversation.user_email} • {formatDate(selectedConversation.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={selectedConversation.status}
                          onChange={(e) => handleStatusUpdate(selectedConversation.conversation_id, e.target.value)}
                          disabled={updating === selectedConversation.conversation_id}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          <option value="new">New</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Conversation */}
                  <div className="p-6">
                    <h4 className="text-sm font-medium text-gray-400 mb-4">Conversation</h4>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                      {selectedConversation.conversation?.map((message, index) => (
                        <div
                          key={index}
                          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] px-4 py-2 rounded-xl ${
                              message.role === 'user'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-200'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-800/30 rounded-2xl border border-gray-700/50 flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-gray-400">Select a conversation to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
    </>
  )

  // If embedded, just return the content
  if (embedded) {
    return content
  }

  // Otherwise, wrap in full page layout
  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      <PageHeader
        title="Help Conversations"
        subtitle="View user help interactions"
        onLogout={onLogout}
        navItems={
          <button
            onClick={onBack}
            className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            ← Back to Dashboard
          </button>
        }
      />
      <main className="flex-1 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-8">
        {content}
      </main>
      <Footer variant="authenticated" />
    </div>
  )
}

AdminHelpConversations.propTypes = {
  onBack: PropTypes.func,
  onLogout: PropTypes.func,
  embedded: PropTypes.bool
}

export default AdminHelpConversations
